import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Divider, Skeleton, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { fetchAdminMessageReadReport } from "../api/adminMessagesApi";

const { Text } = Typography;
const reportCache = new Map();
const REQUEST_TIMEOUT_MS = 12000;

function formatReadAt(value) {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid()) return "—";
  return d.format("DD/MM/YYYY HH:mm");
}

export default function MessageReadReportPanel({ messageId, initialCounts = null }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(() => reportCache.get(String(messageId)) || null);

  const summary = useMemo(() => {
    if (!report) return "דוח קריאה";
    return `נקרא על ידי ${report.read_count} מתוך ${report.total_recipients}`;
  }, [report]);

  const summaryFromInitial = useMemo(() => {
    if (!initialCounts) return "";
    const read = Number(initialCounts.read_count);
    const total = Number(initialCounts.total_recipients);
    if (!Number.isFinite(read) || !Number.isFinite(total)) return "";
    return `נקרא על ידי ${read} מתוך ${total}`;
  }, [initialCounts]);

  useEffect(() => {
    const key = String(messageId);
    setReport(reportCache.get(key) || null);
    setError("");
    setLoading(false);
    setExpanded(false);
  }, [messageId]);

  const loadReport = useCallback(async (force = false) => {
    const key = String(messageId);
    if (!force && reportCache.has(key)) {
      setReport(reportCache.get(key));
      return;
    }
    const requestStartedAt = Date.now();
    setLoading(true);
    setError("");
    if (process.env.NODE_ENV === "development") {
      console.info("[READ_REPORT_PANEL]", {
        messageId,
        opened: true,
        requestStartedAt,
      });
    }
    try {
      const timeoutPromise = new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error("timeout")), REQUEST_TIMEOUT_MS);
      });
      const res = await Promise.race([fetchAdminMessageReadReport(messageId), timeoutPromise]);
      const data = res?.data || null;
      reportCache.set(key, data);
      setReport(data);
      if (process.env.NODE_ENV === "development") {
        console.info("[READ_REPORT_PANEL]", {
          messageId,
          responseMs: Date.now() - requestStartedAt,
          read_count: data?.read_count ?? null,
          unread_count: data?.unread_count ?? null,
          error: null,
        });
      }
    } catch (err) {
      const msg =
        String(err?.message || "").toLowerCase().includes("timeout")
          ? "הבקשה לנתוני קריאה נמשכה יותר מדי זמן"
          : "לא הצלחנו לטעון נתוני קריאה";
      setError(msg);
      if (process.env.NODE_ENV === "development") {
        console.info("[READ_REPORT_PANEL]", {
          messageId,
          responseMs: Date.now() - requestStartedAt,
          error: msg,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    if (!expanded || report || loading) return;
    loadReport(false);
  }, [expanded, report, loading, messageId, loadReport]);

  return (
    <div className="hr-msg-read-report" dir="rtl">
      <Button
        size="small"
        type={expanded ? "default" : "dashed"}
        onClick={() => setExpanded((v) => !v)}
      >
        {report ? summary : summaryFromInitial || summary}
      </Button>

      {expanded ? (
        <div className="hr-msg-read-report__body">
          {loading ? (
            <div className="hr-msg-read-report__loading">
              <Skeleton active paragraph={{ rows: 3 }} title={false} />
            </div>
          ) : null}

          {!loading && error ? (
            <Alert
              type="error"
              showIcon
              message={error}
              action={
                <Button size="small" type="primary" onClick={() => loadReport(true)}>
                  נסה שוב
                </Button>
              }
            />
          ) : null}

          {!loading && !error && report ? (
            <>
              <div className="hr-msg-read-report__counts">
                <Tag color="green">נקראו: {report.read_count}</Tag>
                <Tag color="gold">לא נקראו: {report.unread_count}</Tag>
                <Tag>סה״כ נמענים: {report.total_recipients}</Tag>
              </div>

              <div className="hr-msg-read-report__columns">
                <div className="hr-msg-read-report__col">
                  <strong>נקראו</strong>
                  {Array.isArray(report.read_users) && report.read_users.length > 0 ? (
                    <ul className="hr-msg-read-report__list">
                      {report.read_users.map((u) => (
                        <li key={`read-${u.email || u.user_id}`}>
                          <div>{u.name || "—"}</div>
                          <div className="hr-msg-read-report__meta">{u.email || "—"}</div>
                          <div className="hr-msg-read-report__meta">
                            נקרא: {formatReadAt(u.read_at)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Text type="secondary">אין נמענים שקראו</Text>
                  )}
                </div>

                <Divider type="vertical" className="hr-msg-read-report__divider" />

                <div className="hr-msg-read-report__col">
                  <strong>לא נקראו</strong>
                  {Array.isArray(report.unread_users) && report.unread_users.length > 0 ? (
                    <ul className="hr-msg-read-report__list">
                      {report.unread_users.map((u) => (
                        <li key={`unread-${u.email || u.user_id}`}>
                          <div>{u.name || "—"}</div>
                          <div className="hr-msg-read-report__meta">{u.email || "—"}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Text type="secondary">כל הנמענים קראו</Text>
                  )}
                </div>
              </div>
            </>
          ) : null}

          {!loading && !error && !report ? (
            <Text type="secondary">אין נתוני קריאה זמינים</Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

